import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { CASE_STUDIES, type CaseStudy } from "@/data/caseStudies";
import {
  CITY_HUBS,
  getCityHub,
  regionalKeywordsFor,
  type CityHub,
} from "@/data/cityHubs";
import { ArrowRight, MapPin } from "lucide-react";
import hero from "@/assets/original/hero-shade.jpg";

interface MatchedCase {
  study: CaseStudy;
  match: "primary" | "nearby" | "regional";
  badge?: string;
}

function matchCases(hub: CityHub): MatchedCase[] {
  const nearbyHubs = hub.nearby
    .map((s) => getCityHub(s))
    .filter((c): c is CityHub => Boolean(c));
  const regionTerms = regionalKeywordsFor(hub.slug);

  const matched: MatchedCase[] = [];
  for (const study of CASE_STUDIES) {
    if (study.cityHref === `/${hub.slug}`) {
      matched.push({ study, match: "primary" });
      continue;
    }
    const nearbyHit = nearbyHubs.find(
      (n) => study.cityHref === `/${n.slug}`,
    );
    if (nearbyHit) {
      matched.push({
        study,
        match: "nearby",
        badge: `Serving ${hub.name} from ${nearbyHit.name}`,
      });
      continue;
    }
    if (
      !study.cityHref &&
      regionTerms.some((term) =>
        study.location.toLowerCase().includes(term.toLowerCase()),
      )
    ) {
      matched.push({
        study,
        match: "regional",
        badge: `Serving ${hub.name} across ${study.location}`,
      });
    }
  }
  // Primary first, then nearby, then regional
  const order = { primary: 0, nearby: 1, regional: 2 } as const;
  return matched.sort((a, b) => order[a.match] - order[b.match]);
}

export const Route = createFileRoute("/case-studies/in/$city")({
  beforeLoad: ({ params }) => {
    const hub = getCityHub(params.city);
    if (!hub) throw notFound();
  },
  loader: ({ params }) => {
    const hub = getCityHub(params.city)!;
    const cases = matchCases(hub);
    return { hub, cases };
  },
  head: (ctx) => {
    const hub = getCityHub(ctx.params.city);
    if (!hub) return {};
    const cases = matchCases(hub);
    const title = `Case Studies in ${hub.name}, TX — Real Awning, Cover & Sail Shade Projects | Texas Made Shade`;
    const description = `Real Texas Made Shade case studies serving ${hub.name} and ${hub.region} — commercial awnings, sail shades, patio & pergola covers, marine canvas and more. See scope, materials and outcomes from local builds.`;
    const pageUrl = canonicalUrl(`/case-studies/in/${hub.slug}`);

    const itemListElement = cases.map((mc, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: canonicalUrl(`/case-studies/${mc.study.slug}`),
      name: mc.study.title,
      item: {
        "@type": "CreativeWork",
        name: mc.study.title,
        url: canonicalUrl(`/case-studies/${mc.study.slug}`),
        image: mc.study.images[0]?.src
          ? canonicalUrl(mc.study.images[0].src)
          : undefined,
        description: mc.study.summary,
        about: mc.study.category,
        locationCreated: {
          "@type": "Place",
          name: mc.study.location,
        },
        ...(mc.match !== "primary" && mc.badge
          ? { additionalType: mc.badge }
          : {}),
      },
    }));

    return {
      links: [canonicalLink(`/case-studies/in/${hub.slug}`)],
      meta: routeMeta(ctx, {
        title,
        description,
        image: hero,
        socialTitle: `Case Studies in ${hub.name}, TX | Texas Made Shade`,
        socialDescription: description,
        keywords: `case studies ${hub.name}, awning case studies ${hub.name}, ${hub.name} project gallery, ${hub.name} commercial awnings, ${hub.name} sail shades, ${hub.name} patio covers, pergola covers ${hub.name}, Texas Made Shade ${hub.name}`,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `Case Studies in ${hub.name}, TX`,
            url: pageUrl,
            description,
            about: {
              "@type": "Place",
              name: `${hub.name}, TX`,
              address: {
                "@type": "PostalAddress",
                addressLocality: hub.name,
                addressRegion: "TX",
                addressCountry: "US",
              },
            },
            isPartOf: {
              "@type": "WebSite",
              name: "Texas Made Shade",
              url: canonicalUrl("/"),
            },
            mainEntity: {
              "@type": "ItemList",
              name: `Featured projects serving ${hub.name}, TX`,
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              numberOfItems: itemListElement.length,
              itemListElement,
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-4xl font-bold text-navy-deep">City hub not found</h1>
        <p className="mt-4 text-navy-deep/75">
          We don't have a case-study hub for that city yet. See all{" "}
          <Link to="/case-studies" className="text-red underline">
            project case studies
          </Link>
          .
        </p>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-navy-deep">
          Something went wrong
        </h1>
        <p className="mt-4 text-navy-deep/75">{(error as Error)?.message}</p>
      </main>
      <SiteFooter />
    </div>
  ),
  component: CityCaseStudyHub,
});

function CityCaseStudyHub() {
  const { hub, cases } = Route.useLoaderData();
  const primary = cases.filter((c: MatchedCase) => c.match === "primary");
  const serving = cases.filter((c: MatchedCase) => c.match !== "primary");

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {/* HERO */}
        <section className="relative overflow-hidden bg-navy-deep text-white">
          <div className="absolute inset-0">
            <img loading="lazy" decoding="async"
              src={hero}
              alt={`Texas Made Shade case studies in ${hub.name}, TX`}
              className="h-full w-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/80 to-transparent" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
            <div className="inline-flex items-center gap-2 bg-red px-4 py-1.5 font-display text-xs tracking-[0.3em]">
              ★ FIELD REPORTS · {hub.name.toUpperCase()}, TX
            </div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[0.95] sm:text-5xl md:text-6xl">
              Case Studies in {hub.name}, Texas
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-white/85 sm:text-xl">
              Real Texas Made Shade installs serving {hub.name} and {hub.region} —
              commercial awnings, sail shades, patio &amp; pergola covers, marine
              canvas and more. See the scope, materials and outcomes for every
              project.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 text-sm">
              <Link
                to="/case-studies"
                className="inline-flex items-center gap-2 border-2 border-white px-5 py-2 font-display uppercase tracking-wider text-white hover:bg-white hover:text-navy-deep"
              >
                All case studies
              </Link>
              <a
                href={`/${hub.slug}`}
                className="inline-flex items-center gap-2 bg-red px-5 py-2 font-display uppercase tracking-wider text-white hover:bg-red/90"
              >
                Visit our {hub.name} page <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div className="stripe-bar absolute bottom-0 left-0 h-2 w-full" />
        </section>

        {/* PRIMARY MATCHES */}
        <section className="bg-cream py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="mb-8 flex items-end justify-between gap-4">
              <h2 className="font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
                Built in {hub.name}
              </h2>
              <span className="font-display text-xs uppercase tracking-[0.3em] text-navy-deep/60">
                {primary.length} project{primary.length === 1 ? "" : "s"}
              </span>
            </div>

            {primary.length === 0 ? (
              <div className="border-2 border-dashed border-navy-deep/25 bg-white p-8 text-center">
                <p className="font-display text-lg text-navy-deep">
                  We're working on a published case study from {hub.name}.
                </p>
                <p className="mt-2 text-navy-deep/75">
                  Our crews build and install in {hub.name} regularly — see
                  nearby projects below, or be the next {hub.name} feature.
                </p>
                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center gap-2 bg-red px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-red/90"
                >
                  Start a {hub.name} project <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <CaseGrid items={primary} />
            )}
          </div>
        </section>

        {/* NEARBY / REGIONAL */}
        {serving.length > 0 && (
          <section className="bg-white py-16 lg:py-20">
            <div className="mx-auto max-w-7xl px-4 lg:px-8">
              <div className="mb-8 flex items-end justify-between gap-4">
                <h2 className="font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
                  Also Serving {hub.name}
                </h2>
                <span className="font-display text-xs uppercase tracking-[0.3em] text-navy-deep/60">
                  {serving.length} nearby
                </span>
              </div>
              <p className="mb-8 max-w-3xl text-navy-deep/75">
                Recent installs in nearby cities and across the region. The
                same crew that built these can build for {hub.name}.
              </p>
              <CaseGrid items={serving} />
            </div>
          </section>
        )}

        {/* OTHER CITY HUBS */}
        <section className="bg-navy-deep py-14 text-white">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ Other Texas City Hubs
            </div>
            <h2 className="mt-2 font-display text-2xl font-bold uppercase md:text-3xl">
              Browse case studies by city
            </h2>
            <ul className="mt-6 flex flex-wrap gap-2">
              {CITY_HUBS.filter((c) => c.slug !== hub.slug).map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/case-studies/in/$city"
                    params={{ city: c.slug }}
                    className="inline-block border border-white/30 px-3 py-1.5 font-display text-xs uppercase tracking-wider text-white hover:border-red hover:text-red"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <LetsTalkForm />
      </main>
      <SiteFooter />
    </div>
  );
}

function CaseGrid({ items }: { items: MatchedCase[] }) {
  return (
    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      {items.map(({ study: c, match, badge }) => (
        <Link
          key={c.slug}
          to="/case-studies/$slug"
          params={{ slug: c.slug }}
          className="group flex flex-col overflow-hidden border-2 border-navy-deep/15 bg-white shadow-card transition hover:border-red hover:shadow-bold"
        >
          <div className="relative aspect-[4/3] overflow-hidden bg-navy-deep">
            <img
              src={c.images[0]?.src}
              alt={c.images[0]?.alt ?? c.title}
              width={800}
              height={600}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            />
            <span className="absolute left-3 top-3 bg-red px-2 py-0.5 font-display text-[10px] uppercase tracking-[0.2em] text-white">
              {c.category}
            </span>
            {match !== "primary" && badge && (
              <span className="absolute right-3 top-3 bg-navy-deep/90 px-2 py-0.5 font-display text-[10px] uppercase tracking-[0.2em] text-white">
                Serving
              </span>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-3 p-5">
            <div className="flex items-center gap-1.5 font-display text-[11px] uppercase tracking-[0.25em] text-navy-deep/60">
              <MapPin className="h-3 w-3 text-red" /> {c.location} · {c.year}
            </div>
            <h3 className="font-display text-xl font-bold leading-tight text-navy-deep group-hover:text-red">
              {c.title}
            </h3>
            <p className="text-sm text-navy-deep/75">{c.summary}</p>
            {match !== "primary" && badge && (
              <p className="text-xs italic text-navy-deep/60">{badge}</p>
            )}
            <div className="mt-auto inline-flex items-center gap-1 font-display text-xs uppercase tracking-[0.2em] text-red">
              Read the case study <ArrowRight className="h-3 w-3" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
